import React, { useState, useMemo } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { Calculator } from "lucide-react";

const currency = (n) =>
  (isFinite(n) ? n : 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
const currency2 = (n) =>
  (isFinite(n) ? n : 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const LOAN_TYPES = [
  { id: "va", label: "VA" },
  { id: "fha", label: "FHA" },
  { id: "conventional", label: "Conventional" },
  { id: "usda", label: "USDA" },
];

const monthlyPI = (principal, rate, term) => {
  const r = rate / 100 / 12;
  const n = term * 12;
  if (r <= 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
};

// Returns { principal, upfrontFee, upfrontLabel, monthlyMI, monthlyMILabel }
const computeLoan = (type, base, homePrice, opts) => {
  switch (type) {
    case "va": {
      const feeRate = opts.vaFundingType === "first" ? 0.0215 : 0.033;
      const upfrontFee = base * feeRate;
      const principal = opts.vaFinanceFee === "yes" ? base + upfrontFee : base;
      return {
        principal,
        upfrontFee,
        upfrontLabel: `VA Funding Fee${opts.vaFinanceFee === "yes" ? " (financed)" : ""}`,
        monthlyMI: 0,
        monthlyMILabel: null,
      };
    }
    case "fha": {
      const upfrontFee = base * 0.0175; // upfront MIP, financed
      const principal = base + upfrontFee;
      const monthlyMI = (base * 0.0055) / 12; // annual MIP ~0.55%
      return {
        principal,
        upfrontFee,
        upfrontLabel: "Upfront MIP (1.75%, financed)",
        monthlyMI,
        monthlyMILabel: "Monthly MIP",
      };
    }
    case "usda": {
      const upfrontFee = base * 0.01; // guarantee fee, financed
      const principal = base + upfrontFee;
      const monthlyMI = (base * 0.0035) / 12; // annual fee 0.35%
      return {
        principal,
        upfrontFee,
        upfrontLabel: "Guarantee Fee (1%, financed)",
        monthlyMI,
        monthlyMILabel: "Monthly Guarantee Fee",
      };
    }
    case "conventional":
    default: {
      const ltv = homePrice > 0 ? base / homePrice : 0;
      const monthlyMI = ltv > 0.8 ? (base * 0.006) / 12 : 0; // PMI ~0.6% if <20% down
      return {
        principal: base,
        upfrontFee: 0,
        upfrontLabel: null,
        monthlyMI,
        monthlyMILabel: ltv > 0.8 ? "Monthly PMI" : null,
      };
    }
  }
};

const VACalculator = () => {
  const [loanType, setLoanType] = useState("va");
  const [homePrice, setHomePrice] = useState(320000);
  const [downMode, setDownMode] = useState("dollar");
  const [downValue, setDownValue] = useState(0);
  const [term, setTerm] = useState(30);
  const [rate, setRate] = useState(6.25);
  const [vaFundingType, setVaFundingType] = useState("first");
  const [vaFinanceFee, setVaFinanceFee] = useState("yes");

  const downDollars = downMode === "percent" ? (homePrice * downValue) / 100 : downValue;

  const handleDownModeChange = (mode) => {
    if (!mode || mode === downMode) return;
    if (mode === "percent") {
      setDownValue(homePrice > 0 ? Math.round((downValue / homePrice) * 1000) / 10 : 0);
    } else {
      setDownValue(Math.round((homePrice * downValue) / 100));
    }
    setDownMode(mode);
  };

  const result = useMemo(() => {
    const base = Math.max(homePrice - downDollars, 0);
    const loan = computeLoan(loanType, base, homePrice, { vaFundingType, vaFinanceFee });
    const pi = monthlyPI(loan.principal, rate, term);
    return { ...loan, pi, total: pi + loan.monthlyMI };
  }, [loanType, homePrice, downDollars, term, rate, vaFundingType, vaFinanceFee]);

  const activeLabel = LOAN_TYPES.find((l) => l.id === loanType)?.label;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
      {/* Inputs */}
      <div className="lg:col-span-3 bg-white rounded-2xl p-7 md:p-9 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.35)] border border-[#efe6d6]">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-[#f7edcf] flex items-center justify-center">
            <Calculator className="w-5 h-5 text-gold" />
          </div>
          <h3 className="font-display text-2xl text-[#141210]">Home Loan Calculator</h3>
        </div>

        {/* Loan type toggle */}
        <div className="mb-7">
          <Label className="text-xs tracking-wide uppercase text-[#4a423a] mb-2 block">Loan Type</Label>
          <ToggleGroup
            type="single"
            value={loanType}
            onValueChange={(v) => v && setLoanType(v)}
            className="grid grid-cols-4 gap-2"
          >
            {LOAN_TYPES.map((lt) => (
              <ToggleGroupItem
                key={lt.id}
                value={lt.id}
                className="h-11 rounded-xl border border-[#e0d6c5] text-[#4a423a] text-xs md:text-sm tracking-wide uppercase data-[state=on]:bg-[#141210] data-[state=on]:text-[#d4af37] data-[state=on]:border-[#141210] hover:bg-[#f7edcf]"
              >
                {lt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Home Price ($)</Label>
            <Input
              type="number"
              value={homePrice}
              onChange={(e) => setHomePrice(Number(e.target.value))}
              className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Down Payment</Label>
              <ToggleGroup
                type="single"
                value={downMode}
                onValueChange={handleDownModeChange}
                className="gap-1"
              >
                <ToggleGroupItem
                  value="dollar"
                  className="h-6 w-8 px-0 rounded-md border border-[#e0d6c5] text-[12px] text-[#4a423a] data-[state=on]:bg-[#141210] data-[state=on]:text-[#d4af37] data-[state=on]:border-[#141210]"
                >
                  $
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="percent"
                  className="h-6 w-8 px-0 rounded-md border border-[#e0d6c5] text-[12px] text-[#4a423a] data-[state=on]:bg-[#141210] data-[state=on]:text-[#d4af37] data-[state=on]:border-[#141210]"
                >
                  %
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="relative">
              <Input
                type="number"
                value={downValue}
                onChange={(e) => setDownValue(Number(e.target.value))}
                className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11 pr-9"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#9a8f7d]">
                {downMode === "percent" ? "%" : "$"}
              </span>
            </div>
            <p className="text-[11px] text-[#9a8f7d]">
              {downMode === "percent"
                ? `= ${currency(downDollars)} down`
                : `= ${homePrice > 0 ? ((downValue / homePrice) * 100).toFixed(1) : 0}% down`}
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Loan Term</Label>
            <RadioGroup
              value={String(term)}
              onValueChange={(v) => setTerm(Number(v))}
              className="flex gap-4 pt-2"
            >
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4a423a]">
                <RadioGroupItem value="30" className="text-[#d4af37] border-[#d4af37]" /> 30 years
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4a423a]">
                <RadioGroupItem value="15" className="text-[#d4af37] border-[#d4af37]" /> 15 years
              </label>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Interest Rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              className="bg-[#faf7f2] border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11"
            />
          </div>
        </div>

        {/* VA-specific options */}
        {loanType === "va" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5 pt-5 border-t border-[#efe6d6]">
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-[#4a423a]">VA Funding Fee</Label>
              <RadioGroup value={vaFundingType} onValueChange={setVaFundingType} className="flex flex-col gap-2 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4a423a]">
                  <RadioGroupItem value="first" className="text-[#d4af37] border-[#d4af37]" /> First time (2.15%)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4a423a]">
                  <RadioGroupItem value="subsequent" className="text-[#d4af37] border-[#d4af37]" /> Subsequent (3.3%)
                </label>
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-wide uppercase text-[#4a423a]">Finance Funding Fee?</Label>
              <RadioGroup value={vaFinanceFee} onValueChange={setVaFinanceFee} className="flex gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4a423a]">
                  <RadioGroupItem value="yes" className="text-[#d4af37] border-[#d4af37]" /> Yes
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4a423a]">
                  <RadioGroupItem value="no" className="text-[#d4af37] border-[#d4af37]" /> No
                </label>
              </RadioGroup>
            </div>
          </div>
        )}

        {loanType !== "va" && (
          <p className="text-[11px] text-[#9a8f7d] mt-5 pt-5 border-t border-[#efe6d6] leading-relaxed">
            {loanType === "fha" && "FHA includes a 1.75% upfront MIP (financed) plus monthly mortgage insurance. Scores as low as 500 may qualify with my flexible lender."}
            {loanType === "conventional" && "Conventional loans add monthly PMI when your down payment is under 20% — and PMI drops off as you build equity."}
            {loanType === "usda" && "USDA offers $0 down in eligible rural areas, with a 1% upfront guarantee fee (financed) and a low annual fee."}
          </p>
        )}
      </div>

      {/* Result */}
      <div className="lg:col-span-2 rounded-2xl p-8 bg-[#141210] text-[#e9dcc4] flex flex-col justify-center shadow-[0_20px_50px_-30px_rgba(0,0,0,0.6)]">
        <p className="text-xs tracking-luxury uppercase text-[#a8967a] mb-2">
          Est. Monthly Payment · {activeLabel}
        </p>
        <div className="font-display text-5xl md:text-6xl text-[#d4af37] mb-6">
          {currency2(result.total)}
        </div>
        <div className="h-px bg-[#2a241d] mb-6" />
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[#a8967a]">Principal & Interest</span>
            <span className="text-[#f4ebd9] font-medium">{currency2(result.pi)}</span>
          </div>
          {result.monthlyMILabel && (
            <div className="flex items-center justify-between">
              <span className="text-[#a8967a]">{result.monthlyMILabel}</span>
              <span className="text-[#f4ebd9] font-medium">{currency2(result.monthlyMI)}</span>
            </div>
          )}
          {result.upfrontLabel && (
            <div className="flex items-center justify-between">
              <span className="text-[#a8967a]">{result.upfrontLabel}</span>
              <span className="text-[#f4ebd9] font-medium">{currency(result.upfrontFee)}</span>
            </div>
          )}
        </div>
        <p className="text-[11px] text-[#8a7f6c] mt-6 leading-relaxed">
          Estimates only — actual rates & fees vary. Let's get you a real quote from my flexible lender.
        </p>
      </div>
    </div>
  );
};

export default VACalculator;
